import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Select } from 'antd';

import UserChip, { userLabel } from './UserChip';
import UserAvatar from './UserAvatar';
import useCollaborators from '../../hooks/useCollaborators';
import { getUsersByIds, searchUsersByEmail } from '../../services/users';
import { getUserId } from '../../utils';
import { normalizeId } from '../../utils/ids';
import { ME_VALUE, isHexId } from '../../utils/userElements';
import { USER_ACCOUNT } from '../icons';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEBOUNCE_MS = 300;

/**
 * The account picker behind a `user` element (docs/plans/element-sets-and-me.md,
 * "Who can be picked"): the collection's COLLABORATORS first — owner, grant
 * holders, group members, the caller — as avatar + name rows, and, when what
 * is typed is a full email address, any account found by exact address
 * (the sharing search's rule; no browsing of the user base). Typing
 * anything else narrows the collaborators by name, client-side.
 *
 * Two shapes of "Me", chosen by the surface:
 *   meMode 'shortcut'  a "Me" row at the top that picks the CALLER'S ID —
 *                      the item form, the multi add form, a Fixed quick add
 *                      default. Hidden once the caller is already picked.
 *   meMode 'value'     a "Me" row whose value is the KEYWORD `me` (ME_VALUE)
 *                      — the filter builder and the quick-filter chip, where
 *                      the condition is "whoever is signed in".
 *
 * Props mirror antd Select where it matters: `value` / `onChange` (an id, or
 * a list in `multiple`), `placeholder`, `disabled`, `size`, `id`.
 *
 * `onlyAccountIds` is for a surface that can hold TEXT where an id belongs (the
 * condition builder, KPR-143: a rule typed as `assignee = zoe`). That text is
 * shown as it was written, but it is never sent to the by-id lookup, which the
 * API answers with a 400 for the whole batch when one entry is not a 24-hex id.
 */

const shape = user => ({ user, id: normalizeId(user?._id) });

/** One option row: avatar + name, non-interactive (the row's job is to be picked). */
export function UserPickerRow({ user }) {
	return <UserChip user={user} interactive={false} size={20} className="user-picker__row" />;
}

function MeRow({ label }) {
	return (
		<span className="user-chip user-picker__row user-picker__me">
			<span className="user-picker__me-icon" aria-hidden="true"><USER_ACCOUNT /></span>
			<span className="user-chip__name">{label}</span>
		</span>
	);
}

export default function UserPickerSelect({
	collectionId,
	value,
	onChange,
	multiple = false,
	meMode = null,
	meLabel = 'Me',
	placeholder = 'Pick a person, or type a full email address',
	disabled = false,
	size,
	id,
	className,
	style,
	'aria-label': ariaLabel,
	'data-testid': testId,
	autoFocus = false,
	open,
	onOpenChange,
	status,
	onBlur,
	onlyAccountIds = false,
}) {
	const { users: collaborators, loading: rosterLoading } = useCollaborators(collectionId);
	const [term, setTerm] = useState('');
	const [emailResults, setEmailResults] = useState([]);
	const [searching, setSearching] = useState(false);
	const [known, setKnown] = useState({}); // id -> public user, for values not on the roster
	const debounceRef = useRef(null);
	const requestSeq = useRef(0);

	const callerId = normalizeId(getUserId());

	const valueIds = useMemo(
		() => (Array.isArray(value) ? value : (value ? [value] : [])).map(v => (v === ME_VALUE ? v : normalizeId(v))).filter(Boolean),
		[value]
	);

	// Values that are on nobody's roster (a former collaborator, an account
	// found by email in an earlier session) still read as a name.
	const rosterById = useMemo(() => {
		const map = new Map();
		collaborators.forEach(user => { const s = shape(user); if (s.id) map.set(s.id, user); });
		emailResults.forEach(user => { const s = shape(user); if (s.id) map.set(s.id, user); });
		return map;
	}, [collaborators, emailResults]);

	useEffect(() => {
		if (rosterLoading) return undefined;
		const missing = valueIds.filter(v => v !== ME_VALUE && !rosterById.has(v) && !(v in known) && (!onlyAccountIds || isHexId(v)));
		if (!missing.length) return undefined;
		let active = true;
		getUsersByIds(missing).then(users => {
			if (!active) return;
			setKnown(prev => {
				const next = { ...prev };
				missing.forEach(v => { if (!(v in next)) next[v] = null; });
				users.forEach(user => { const s = shape(user); if (s.id) next[s.id] = user; });
				return next;
			});
		}).catch(() => {});
		return () => { active = false; };
	}, [valueIds, rosterById, known, rosterLoading, onlyAccountIds]);

	const runEmailSearch = useCallback(text => {
		const seq = ++requestSeq.current;
		if (!EMAIL_RE.test(text)) {
			setEmailResults([]);
			setSearching(false);
			return;
		}
		setSearching(true);
		searchUsersByEmail(text)
			.then(users => { if (seq === requestSeq.current) setEmailResults(users || []); })
			.catch(() => { if (seq === requestSeq.current) setEmailResults([]); })
			.finally(() => { if (seq === requestSeq.current) setSearching(false); });
	}, []);

	const handleSearch = useCallback(text => {
		setTerm(text);
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => runEmailSearch((text || '').trim()), DEBOUNCE_MS);
	}, [runEmailSearch]);

	useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

	const options = useMemo(() => {
		const needle = (term || '').trim().toLowerCase();
		const byId = new Map();
		const add = user => {
			const s = shape(user);
			if (!s.id || byId.has(s.id)) return;
			byId.set(s.id, { value: s.id, label: <UserPickerRow user={user} />, title: userLabel(user), searchText: userLabel(user).toLowerCase() });
		};
		// The picked values first (so they always have a label), then the
		// roster narrowed by what is typed, then anyone found by email.
		valueIds.forEach(v => {
			if (v === ME_VALUE) return;
			add(rosterById.get(v) || known[v] || { _id: v, fullName: v });
		});
		collaborators.forEach(user => {
			if (needle && EMAIL_RE.test(needle)) return; // an address is a different question
			if (needle && !userLabel(user).toLowerCase().includes(needle)) return;
			add(user);
		});
		emailResults.forEach(add);
		const rows = [...byId.values()];

		const showMe = meMode === 'shortcut'
			? !!callerId && !valueIds.includes(callerId) && (!needle || meLabel.toLowerCase().includes(needle))
			: meMode === 'value' && !valueIds.includes(ME_VALUE) && (!needle || meLabel.toLowerCase().includes(needle));
		if (meMode === 'value' && valueIds.includes(ME_VALUE)) {
			rows.unshift({ value: ME_VALUE, label: <MeRow label={meLabel} />, title: meLabel });
		} else if (showMe) {
			rows.unshift({
				value: meMode === 'shortcut' ? `${ME_VALUE}:${callerId}` : ME_VALUE,
				label: <MeRow label={meLabel} />,
				title: meLabel,
			});
		}
		return rows;
	}, [term, valueIds, rosterById, known, collaborators, emailResults, meMode, meLabel, callerId]);

	const handleChange = useCallback(next => {
		// The shortcut row carries the caller's id behind a marker so it can
		// sit beside the same person's roster row without antd deduping them.
		const unwrap = v => (typeof v === 'string' && v.startsWith(`${ME_VALUE}:`) ? v.slice(ME_VALUE.length + 1) : v);
		if (multiple) {
			const ids = (Array.isArray(next) ? next : []).map(unwrap);
			onChange([...new Set(ids)]);
			return;
		}
		onChange(next === undefined || next === null ? '' : unwrap(next));
	}, [multiple, onChange]);

	const notFound = searching
		? 'Searching…'
		: (rosterLoading ? 'Loading people…'
			: (EMAIL_RE.test((term || '').trim())
				? 'No account has that email address.'
				: 'No collaborator matches. Type a full email address to find anyone.'));

	return (
		<Select
			id={id}
			showSearch
			size={size}
			mode={multiple ? 'multiple' : undefined}
			value={multiple ? valueIds : (valueIds[0] ?? undefined)}
			onChange={handleChange}
			onSearch={handleSearch}
			onBlur={event => { setTerm(''); if (typeof onBlur === 'function') onBlur(event); }}
			status={status}
			filterOption={false}
			loading={searching || rosterLoading}
			options={options}
			optionLabelProp="label"
			placeholder={placeholder}
			disabled={disabled}
			allowClear
			autoFocus={autoFocus}
			open={open}
			onDropdownVisibleChange={onOpenChange}
			className={['user-picker', className].filter(Boolean).join(' ')}
			style={style}
			aria-label={ariaLabel}
			data-testid={testId}
			notFoundContent={notFound}
			// The tag a picked person wears in multi mode: avatar + name.
			tagRender={multiple ? ({ value: tagValue, label, closable, onClose }) => {
				const user = rosterById.get(tagValue) || known[tagValue];
				return (
					<span className="ant-select-selection-item user-picker__tag" title={user ? userLabel(user) : tagValue}>
						<span className="ant-select-selection-item-content">
							{tagValue === ME_VALUE ? label : (
								<span className="user-chip user-chip--tag">
									<UserAvatar user={user} size={18} />
									<span className="user-chip__name">{user ? userLabel(user) : tagValue}</span>
								</span>
							)}
						</span>
						{closable ? (
							<span className="ant-select-selection-item-remove" role="button" aria-label={`Remove ${user ? userLabel(user) : tagValue}`} onClick={onClose}>×</span>
						) : null}
					</span>
				);
			} : undefined}
		/>
	);
}
